import React from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';

/**
 * Generic right-side detail sheet — roomy 2-column layout.
 *
 * Earlier this component used a narrow 440px panel which felt cramped when
 * displaying 10+ rows plus a child block (like the billing timeline or the
 * recent activities). We now default to ~760px on desktop and let pages opt
 * into an even wider variant via the `wide` prop for full dashboards.
 *
 * Props:
 *  - open, onOpenChange
 *  - title, subtitle
 *  - rows: [{ label, value }]   key-value pairs rendered as a responsive grid
 *  - badge?: ReactNode (status badge)
 *  - actions?: ReactNode (buttons aligned top-right, e.g. Download PDF)
 *  - children?: extra content stretched below
 *  - wide?: boolean — use the 960px XL variant
 */
export default function DetailSheet({
  open,
  onOpenChange,
  title,
  subtitle,
  rows = [],
  badge,
  actions,
  children,
  wide = false,
}) {
  const widthClass = wide
    ? 'w-full sm:w-[720px] md:w-[960px] sm:max-w-[960px]'
    : 'w-full sm:w-[560px] md:w-[760px] sm:max-w-[760px]';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className={`${widthClass} p-0 overflow-y-auto bg-gradient-to-br from-white via-white to-[#FAFAFE]`}
      >
        {/* Sticky header */}
        <SheetHeader className="px-7 pt-7 pb-5 border-b border-[#EFF0F6] bg-white/95 backdrop-blur-sm sticky top-0 z-10">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              {badge && <div className="mb-2">{badge}</div>}
              <SheetTitle
                className="text-[22px] leading-tight break-words"
                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              >
                {title}
              </SheetTitle>
              {subtitle && (
                <SheetDescription className="text-[13px] mt-1">{subtitle}</SheetDescription>
              )}
            </div>
            {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
          </div>
        </SheetHeader>

        {/* Body — rows in a 2-column grid on sm+ for better density */}
        <div className="px-7 py-6">
          {rows.length > 0 && (
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
              {rows.map((r, i) => (
                <div
                  key={i}
                  className="flex flex-col gap-0.5 py-2 border-b border-[#F3F4F8] last:border-0 sm:[&:nth-last-child(2)]:border-0"
                >
                  <dt className="text-[11px] font-semibold uppercase tracking-wider text-[#8E8FA5]">
                    {r.label}
                  </dt>
                  <dd className="text-[13.5px] font-medium text-[#1A1B2E] break-words">
                    {r.value ?? '—'}
                  </dd>
                </div>
              ))}
            </dl>
          )}
          {children && <div className="mt-6">{children}</div>}
        </div>
      </SheetContent>
    </Sheet>
  );
}
